import { useEffect, useState } from 'react';

import { useConfig } from '../defaultConfig';
import { useAccountNumber } from '../fetchHooks/useAccount';
import { useCrossBorderReceiver } from '../fetchHooks/useCrossBorderReceivers';
import useOcr from '../fetchHooks/useOcr';
import { useSingleReceiver } from '../fetchHooks/useReceivers';
import type { TransactionActor } from '../generated-types';
import isDomesticAccountNumber from '../utils/boolean/isDomesticAccountNumber';

export interface UseTransactionActorResult {
    transactionActor?: TransactionActor;
    isLoading: boolean;
}

const useTransactionActor = (
    toAccount: TransactionActor | undefined,
    ocrCheck = true,
    enabled = true,
): UseTransactionActorResult => {
    const { featureToggles } = useConfig();
    const [finalActor, setFinalActor] = useState<TransactionActor | undefined>(toAccount);

    const accountQuery = useAccountNumber(toAccount?.accountNumber, enabled);
    const needReceiver = accountQuery.isFetched && !accountQuery.data?.alias && !finalActor?.name;
    const receiverQuery = useSingleReceiver(toAccount?.accountNumber, needReceiver && enabled);
    const needsOcrRecord =
        enabled &&
        ocrCheck &&
        receiverQuery.isFetched &&
        !receiverQuery.data &&
        !finalActor?.name &&
        !!toAccount?.accountNumber &&
        isDomesticAccountNumber(toAccount.accountNumber);

    const crossBorderQuery = useCrossBorderReceiver(
        toAccount?.iban,
        featureToggles.enableCreateCrossBorderReceiver && !finalActor?.name && enabled,
    );
    const ocrQuery = useOcr(toAccount?.accountNumber, needsOcrRecord);

    useEffect(() => {
        if (toAccount?.name) {
            setFinalActor(toAccount);
        } else if (accountQuery.data?.alias) {
            setFinalActor({ ...toAccount, name: accountQuery.data.alias });
        } else if (receiverQuery.data || ocrQuery.data || ocrQuery.isFetched || crossBorderQuery.isFetched) {
            const actor: TransactionActor = {
                ...toAccount,
                name: ocrQuery.data?.name ?? receiverQuery.data?.name ?? crossBorderQuery?.data?.name,
            };
            setFinalActor(actor);
        }
    }, [
        accountQuery?.data?.alias,
        toAccount,
        receiverQuery.data,
        ocrQuery.data,
        ocrQuery.isFetched,
        crossBorderQuery.isFetched,
        crossBorderQuery?.data?.name,
    ]);

    return {
        transactionActor: finalActor,
        isLoading:
            (accountQuery.isLoading || ocrQuery.isLoading || receiverQuery.isLoading || crossBorderQuery.isLoading) &&
            enabled,
    };
};
export default useTransactionActor;
